import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { usersApi, shelfApi, reviewsApi } from '@/lib/api';
import AppNavbar from '@/components/app/AppNavbar';
import BookCard from '@/components/app/BookCard';
import StarRating from '@/components/app/StarRating';
import TimeAgo from '@/components/app/TimeAgo';
import FollowersModal from '@/components/app/FollowersModal';
import MobileBottomNav from '@/components/app/MobileBottomNav';
import SEOMeta from '@/components/app/SEOMeta';
import ReadingGoalWidget from '@/components/app/ReadingGoalWidget';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Settings, UserPlus, UserCheck, BookOpen, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

const SHELF_TABS = [
  { key: 'read', label: 'Read' },
  { key: 'reading', label: 'Reading' },
  { key: 'want', label: 'Want to Read' },
  { key: 'own', label: 'Own' },
  { key: 'dnf', label: 'Did Not Finish' },
];

export default function ProfilePage() {
  const { username } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [shelf, setShelf] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingShelf, setLoadingShelf] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [followersModal, setFollowersModal] = useState(null); // 'followers' | 'following' | null

  const isOwnProfile = currentUser?.username === username;

  useEffect(() => {
    const fetchProfile = async () => {
      setLoadingProfile(true);
      try {
        const data = await usersApi.getProfile(username);
        setProfile(data);
        setIsFollowing(data.is_following || false);
        setFollowerCount(data.follower_count || 0);
        setFollowingCount(data.following_count || 0);
      } catch (err) {
        toast.error('Could not load profile');
      } finally {
        setLoadingProfile(false);
      }
    };

    const fetchShelf = async () => {
      setLoadingShelf(true);
      try {
        const data = await shelfApi.getUserShelf(username);
        setShelf(Array.isArray(data) ? data : data?.items || []);
      } catch (_) {
        setShelf([]);
      } finally {
        setLoadingShelf(false);
      }
    };

    const fetchReviews = async () => {
      setLoadingReviews(true);
      try {
        const data = await reviewsApi.getByUser(username);
        setReviews(data);
      } catch (_) {
        setReviews([]);
      } finally {
        setLoadingReviews(false);
      }
    };

    fetchProfile();
    fetchShelf();
    fetchReviews();
  }, [username]);

  const handleFollow = async () => {
    if (!currentUser) { navigate('/login'); return; }
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await usersApi.unfollow(username);
        setIsFollowing(false);
        setFollowerCount((c) => Math.max(0, c - 1));
        toast.success(`Unfollowed @${username}`);
      } else {
        await usersApi.follow(username);
        setIsFollowing(true);
        setFollowerCount((c) => c + 1);
        toast.success(`Following @${username}`);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update follow');
    } finally {
      setFollowLoading(false);
    }
  };

  const shelfByState = SHELF_TABS.reduce((acc, tab) => {
    acc[tab.key] = shelf.filter((item) => 
      item.shelf_states && item.shelf_states.includes(tab.key)
    );
    return acc;
  }, {});

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-cream">
        <AppNavbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-start gap-6 mb-8">
            <Skeleton className="w-24 h-24 rounded-full bg-cream-dark" />
            <div className="space-y-3 flex-1">
              <Skeleton className="h-6 w-48 bg-cream-dark" />
              <Skeleton className="h-4 w-32 bg-cream-dark" />
              <Skeleton className="h-4 w-full bg-cream-dark" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-cream">
        <AppNavbar />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h2 className="font-heading text-2xl text-ink mb-2">User not found</h2>
          <p className="text-ink-muted">@{username} doesn&apos;t exist</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pb-20 md:pb-0">
      <SEOMeta
        title={`${profile.display_name || profile.username} (@${profile.username})`}
        description={profile.bio || `${profile.display_name || profile.username} is a reader on ReadTheRoom.`}
        image={profile.avatar_url}
        type="profile"
      />
      <AppNavbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-cream border border-cream-dark rounded-2xl p-6 mb-6"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <Avatar className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0">
              <AvatarImage src={profile.avatar_url} alt={profile.display_name} />
              <AvatarFallback className="bg-terra text-cream text-2xl font-bold">
                {getInitials(profile.display_name || profile.username)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <h1 className="font-heading text-2xl sm:text-3xl text-ink">
                    {profile.display_name || profile.username}
                  </h1>
                  <p className="text-ink-muted text-sm">@{profile.username}</p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {isOwnProfile ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/settings')}
                      className="border-cream-dark text-ink hover:border-terra hover:text-terra"
                    >
                      <Settings className="w-4 h-4 mr-1.5" />
                      Edit Profile
                    </Button>
                  ) : currentUser && (
                    <Button
                      size="sm"
                      onClick={handleFollow}
                      disabled={followLoading}
                      className={
                        isFollowing
                          ? 'border border-terra text-terra bg-transparent hover:bg-terra/10'
                          : 'bg-terra hover:bg-terra/90 text-cream'
                      }
                    >
                      {isFollowing ? (
                        <>
                          <UserCheck className="w-4 h-4 mr-1.5" />
                          Following
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4 mr-1.5" />
                          Follow
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {profile.bio && (
                <p className="text-ink-light text-sm mt-2 leading-relaxed">{profile.bio}</p>
              )}

              {/* Stats */}
              <div className="flex items-center gap-4 mt-3">
                <button
                  onClick={() => setFollowersModal('followers')}
                  className="text-center hover:text-terra transition-colors"
                >
                  <span className="text-sm font-semibold text-ink block">{followerCount}</span>
                  <span className="text-xs text-ink-muted">Followers</span>
                </button>
                <button
                  onClick={() => setFollowersModal('following')}
                  className="text-center hover:text-terra transition-colors"
                >
                  <span className="text-sm font-semibold text-ink block">{followingCount}</span>
                  <span className="text-xs text-ink-muted">Following</span>
                </button>
                <div className="text-center">
                  <span className="text-sm font-semibold text-ink block">{shelf.length}</span>
                  <span className="text-xs text-ink-muted">Books</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Reading Goal */}
        <div className="mb-6">
          <ReadingGoalWidget username={username} isOwn={isOwnProfile} />
        </div>

        {/* Shelf & Content Tabs */}
        <Tabs defaultValue="all" onValueChange={setActiveTab}>
          <TabsList className="bg-cream-dark border border-cream-dark mb-6 h-auto flex-wrap gap-1 p-1">
            {/* All tab */}
            <TabsTrigger
              value="all"
              className="text-xs sm:text-sm data-[state=active]:bg-terra data-[state=active]:text-cream px-3 py-1.5"
            >
              All
              {shelf.length > 0 && (
                <Badge className="ml-1.5 bg-terra/20 text-terra text-xs px-1.5 py-0 h-4">
                  {shelf.length}
                </Badge>
              )}
            </TabsTrigger>
            {SHELF_TABS.map((tab) => (
              <TabsTrigger
                key={tab.key}
                value={tab.key}
                className="text-xs sm:text-sm data-[state=active]:bg-terra data-[state=active]:text-cream px-3 py-1.5"
              >
                {tab.label}
                {shelfByState[tab.key]?.length > 0 && (
                  <Badge className="ml-1.5 bg-terra/20 text-terra text-xs px-1.5 py-0 h-4">
                    {shelfByState[tab.key].length}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
            <TabsTrigger
              value="reviews"
              className="text-xs sm:text-sm data-[state=active]:bg-terra data-[state=active]:text-cream px-3 py-1.5"
            >
              <MessageSquare className="w-3 h-3 mr-1" />
              Reviews
              {reviews.length > 0 && (
                <Badge className="ml-1.5 bg-terra/20 text-terra text-xs px-1.5 py-0 h-4">
                  {reviews.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* All shelf items */}
          <TabsContent value="all">
            {loadingShelf ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="aspect-[2/3] rounded-lg bg-cream-dark" />
                    <Skeleton className="h-3 w-full bg-cream-dark" />
                  </div>
                ))}
              </div>
            ) : shelf.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-10 h-10 text-ink-muted mx-auto mb-3 opacity-50" />
                <p className="text-ink-muted text-sm">
                  {isOwnProfile ? "You haven't added any books yet." : 'No books on shelf yet.'}
                </p>
                {isOwnProfile && (
                  <Button
                    size="sm"
                    className="mt-4 bg-terra hover:bg-terra/90 text-cream"
                    onClick={() => navigate('/search')}
                  >
                    Find books
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                {[...shelf]
                  .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                  .map((item) => (
                    <BookCard
                      key={item.id || item.book?.id}
                      book={item.book || item}
                      shelfState={item.shelf_states?.[0]}
                    />
                  ))}
              </div>
            )}
          </TabsContent>

          {SHELF_TABS.map((tab) => (
            <TabsContent key={tab.key} value={tab.key}>
              {loadingShelf ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton className="aspect-[2/3] rounded-lg bg-cream-dark" />
                      <Skeleton className="h-3 w-full bg-cream-dark" />
                    </div>
                  ))}
                </div>
              ) : shelfByState[tab.key]?.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-10 h-10 text-ink-muted mx-auto mb-3 opacity-50" />
                  <p className="text-ink-muted text-sm">
                    {isOwnProfile
                      ? `You haven't added any books to your ${tab.label} shelf yet.`
                      : `No books in ${tab.label} shelf yet.`}
                  </p>
                  {isOwnProfile && (
                    <Button
                      size="sm"
                      className="mt-4 bg-terra hover:bg-terra/90 text-cream"
                      onClick={() => navigate('/search')}
                    >
                      Find books
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                  {shelfByState[tab.key].map((item) => (
                    <BookCard
                      key={item.id || item.book?.id}
                      book={item.book || item}
                      shelfState={item.shelf_states?.[0]}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          ))}

          <TabsContent value="reviews">
            {loadingReviews ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-24 rounded-xl bg-cream-dark" />
                ))}
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-10 h-10 text-ink-muted mx-auto mb-3 opacity-50" />
                <p className="text-ink-muted text-sm">No reviews yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="bg-cream border border-cream-dark rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      {review.book && (
                        <div
                          onClick={() => navigate(`/books/${review.book.id}`)}
                          className="w-12 h-16 flex-shrink-0 rounded overflow-hidden bg-cream-dark cursor-pointer"
                        >
                          {review.book.cover_url ? (
                            <img src={review.book.cover_url} alt={review.book.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BookOpen className="w-4 h-4 text-ink-muted" />
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex-1">
                        {review.book && (
                          <p
                            className="text-sm font-semibold text-ink font-heading cursor-pointer hover:text-terra"
                            onClick={() => navigate(`/books/${review.book.id}`)}
                          >
                            {review.book.title}
                          </p>
                        )}
                        {review.star_rating > 0 && (
                          <StarRating value={review.star_rating} readonly size="sm" />
                        )}
                        <p className="text-sm text-ink-light mt-1 leading-relaxed">{review.body}</p>
                        <TimeAgo date={review.created_at} className="text-xs mt-2 block" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

        </Tabs>
      </div>

      <FollowersModal
        username={username}
        type={followersModal}
        open={!!followersModal}
        onClose={() => setFollowersModal(null)}
      />

      <MobileBottomNav />
    </div>
  );
}
